// Tableau / Power BI to Sigma migration assessment. Pure functions shared by the pages and tests.
// Produces a readout: relative size, drivers (risks), a phase plan, and what to prepare.
// It deliberately gives no hours, weeks, or prices: those depend on things a form cannot see.

export const SOURCES = {
  tableau: {
    label: 'Tableau',
    reportNoun: 'workbooks',
    calcLabel: 'Calculated fields',
    complexLabel: 'LOD expressions',
    complexHelp: 'FIXED, INCLUDE, or EXCLUDE.',
    windowLabel: 'Table calculations',
    windowHelp: 'Running totals, ranks, percent of total, WINDOW_ functions.',
    paramLabel: 'Parameters',
    extractLabel: 'Data sources on extracts',
    features: [
      { key: 'actions', label: 'Dashboard actions (filter, highlight, URL)' },
      { key: 'prep', label: 'Tableau Prep flows' },
      { key: 'customSql', label: 'Custom SQL in data sources' },
      { key: 'rls', label: 'Row-level security (user filters)' },
      { key: 'embedded', label: 'Dashboards embedded in another app' },
      { key: 'subscriptions', label: 'Email subscriptions and alerts' },
    ],
  },
  powerbi: {
    label: 'Power BI',
    reportNoun: 'reports',
    calcLabel: 'DAX measures',
    complexLabel: 'Measures using CALCULATE or filter functions',
    complexHelp: 'CALCULATE, FILTER, ALL, ALLEXCEPT, USERELATIONSHIP.',
    windowLabel: 'Time intelligence measures',
    windowHelp: 'TOTALYTD, SAMEPERIODLASTYEAR, DATEADD, rolling windows.',
    paramLabel: 'What-if and field parameters',
    extractLabel: 'Semantic models in Import mode',
    features: [
      { key: 'powerQuery', label: 'Power Query (M) transformations' },
      { key: 'dataflows', label: 'Dataflows' },
      { key: 'paginated', label: 'Paginated reports' },
      { key: 'rls', label: 'Row-level security roles' },
      { key: 'embedded', label: 'Reports embedded in another app' },
      { key: 'calcGroups', label: 'Calculation groups' },
    ],
  },
};

export const WAREHOUSES = ['snowflake', 'bigquery', 'databricks', 'redshift', 'other', 'none'];

export const DEFAULTS = Object.freeze({
  reports: 30,
  users: 100,
  sources: 8,
  extracts: 4,
  calcs: 150,
  complex: 20,
  window: 30,
  params: 10,
  warehouse: 'snowflake',
  usageKnown: 'no',
});

const LIMITS = { reports: 5000, users: 100000, sources: 1000, extracts: 1000, calcs: 100000, complex: 100000, window: 100000, params: 10000 };

function count(value, max, fallback) {
  const n = typeof value === 'number' ? value : Number(String(value ?? '').replace(/[,\s]/g, ''));
  if (!Number.isFinite(n) || String(value ?? '').trim() === '') return fallback;
  if (n < 0) return 0;
  return Math.min(Math.round(n), max);
}

// Lowercase the first letter for use mid-sentence, but keep acronyms like "LOD" or "DAX".
function lowerFirst(text) {
  return /^[A-Z]{2}/.test(text) ? text : text.charAt(0).toLowerCase() + text.slice(1);
}

export function sanitize(raw = {}, source = 'tableau') {
  const out = {};
  for (const key of Object.keys(LIMITS)) out[key] = count(raw[key] ?? DEFAULTS[key], LIMITS[key], DEFAULTS[key]);
  out.extracts = Math.min(out.extracts, out.sources);
  out.warehouse = WAREHOUSES.includes(raw.warehouse) ? raw.warehouse : DEFAULTS.warehouse;
  out.usageKnown = raw.usageKnown === 'yes' ? 'yes' : 'no';
  const allowed = SOURCES[source]?.features.map((f) => f.key) ?? [];
  out.features = Array.isArray(raw.features) ? raw.features.filter((f) => allowed.includes(f)) : [];
  return out;
}

function band(n, cuts) {
  let level = 0;
  for (const cut of cuts) if (n > cut) level += 1;
  return level;
}

const fmt = (n) => n.toLocaleString('en-US');

export function assess(raw, source = 'tableau') {
  const src = SOURCES[source] ? source : 'tableau';
  const meta = SOURCES[src];
  const i = sanitize(raw, src);
  const has = (key) => i.features.includes(key);
  const risks = [];
  let points = 0;

  points += band(i.reports, [10, 40, 120]);
  points += band(i.users, [50, 300, 1000]);

  if (i.warehouse === 'none') {
    points += 3;
    risks.push({ level: 'Rebuild', title: 'No cloud warehouse yet', body: 'Sigma queries a cloud data warehouse directly. Choosing one and loading the data comes before any dashboard moves, and it is usually the largest piece of the work.' });
  }

  const extractShare = i.sources ? i.extracts / i.sources : 0;
  if (i.extracts > 0) {
    points += extractShare >= 0.5 ? 2 : 1;
    risks.push({
      level: 'Rebuild',
      title: `${meta.extractLabel}: ${fmt(i.extracts)} of ${fmt(i.sources)}`,
      body: src === 'tableau'
        ? 'Sigma reads live warehouse tables, not extracts. Any joins, filters, or calculations baked into extracts need to move into warehouse tables or dbt models first.'
        : 'Sigma reads live warehouse tables, not imported models. Relationships and transformations in Import-mode models need to move into the warehouse or a Sigma data model.',
    });
  }

  const complexLevel = band(i.complex, [0, 10, 50]);
  points += complexLevel;
  if (i.complex > 0) {
    risks.push({
      level: 'Rebuild',
      title: `${fmt(i.complex)} ${lowerFirst(meta.complexLabel)}`,
      body: src === 'tableau'
        ? 'Sigma has no LOD keywords. Each becomes a grouping level or a lookup, and FIXED results can change once workbook filters apply. Test these first.'
        : 'Filter context does not exist in Sigma. CALCULATE-style measures become conditional aggregates (SumIf, CountIf), groupings, or lookups, and each needs a side-by-side test.',
    });
  }

  const windowLevel = band(i.window, [0, 20, 80]);
  points += windowLevel;
  if (i.window > 0) {
    risks.push({
      level: 'Check',
      title: `${fmt(i.window)} ${lowerFirst(meta.windowLabel)}`,
      body: src === 'tableau'
        ? 'Running totals and percent of total map to CumulativeSum, GrandTotal, and Subtotal, but the partition and sort must be checked one by one.'
        : 'Time intelligence relies on a marked date table. In Sigma, group by the period and use CumulativeSum or DateLookback, then check the first period of each year.',
    });
  }

  if (i.params > 0) {
    risks.push({ level: 'Check', title: `${fmt(i.params)} ${lowerFirst(meta.paramLabel)}`, body: 'These become Sigma controls. Most map cleanly; check any that change which measure or field a chart shows.' });
  }

  const featureNotes = {
    actions: ['Check', 'Dashboard actions', 'Filter and highlight actions become Sigma control and element interactions. URL actions need their links rebuilt.'],
    prep: ['Rebuild', 'Tableau Prep flows', 'Prep logic has to move into the warehouse or dbt so Sigma can read the result. Treat each flow as its own small project.'],
    customSql: ['Check', 'Custom SQL in data sources', 'Custom SQL can usually move into a warehouse view or a Sigma custom SQL element. Review each for hard-coded filters.'],
    powerQuery: ['Rebuild', 'Power Query transformations', 'M transformations run inside Power BI. They need to move into the warehouse or dbt before Sigma can use the data.'],
    dataflows: ['Rebuild', 'Dataflows', 'Dataflow logic needs a new home in the warehouse. List each dataflow, its sources, and what reads it.'],
    paginated: ['Check', 'Paginated reports', 'Pixel-perfect paginated reports need a plan: a Sigma export, a scheduled PDF, or a separate reporting tool.'],
    calcGroups: ['Rebuild', 'Calculation groups', 'Calculation groups have no direct equivalent. Expect to rebuild them as separate metrics or controls.'],
    rls: ['Check', 'Row-level security', 'Rebuild security with Sigma user attributes or warehouse policies, then test it with real accounts before cutover.'],
    embedded: ['Check', 'Embedded analytics', 'Embedding in Sigma uses signed URLs and user attributes. Plan the host app changes alongside the dashboard work.'],
    subscriptions: ['Direct', 'Subscriptions and alerts', 'Recreate schedules and alerts in Sigma after the dashboards are validated.'],
  };
  for (const key of i.features) {
    const [level, title, body] = featureNotes[key];
    if (level === 'Rebuild') points += 2;
    else if (level === 'Check') points += 1;
    risks.push({ level, title, body });
  }

  if (i.calcs > 0) {
    risks.push({
      level: 'Direct',
      title: `${fmt(i.calcs)} ${lowerFirst(meta.calcLabel)}`,
      body: `Most row-level formulas translate directly. Use the ${meta.label === 'Tableau' ? 'Tableau' : 'DAX'} to Sigma formula translator for the rest.`,
    });
  }

  const size = points <= 4 ? 'Small' : points <= 10 ? 'Medium' : 'Large';
  const order = { Rebuild: 0, Check: 1, Direct: 2 };
  risks.sort((a, b) => order[a.level] - order[b.level]);

  const phases = [];
  if (i.warehouse === 'none' || i.extracts > 0 || has('prep') || has('powerQuery') || has('dataflows')) {
    phases.push({ title: 'Prepare the data', body: i.warehouse === 'none' ? 'Pick a warehouse, load the sources, and model the tables the dashboards need.' : 'Move extract, Prep, or Power Query logic into warehouse tables or dbt models.' });
  }
  phases.push({
    title: 'Pilot one dashboard',
    body: `Pick a ${meta.reportNoun.slice(0, -1)} people open every week that uses ${i.complex > 0 ? (src === 'tableau' ? 'an LOD expression' : 'a CALCULATE measure') : 'a few calculations'}${i.params > 0 ? ' and a parameter' : ''}. It tests the hard parts early.`,
  });
  if (size !== 'Small') phases.push({ title: 'Move by team', body: `Group the remaining ${meta.reportNoun} by team or data source, and validate each group side by side before switching.` });
  phases.push({ title: 'Validate and cut over', body: `Compare numbers on the same data cutoff, retire what nobody uses, then switch off ${meta.label}.` });

  const prepare = [];
  if (i.usageKnown === 'no') prepare.push(`A usage export for the last 90 days: which ${meta.reportNoun} people actually open. It usually shrinks the list.`);
  prepare.push('An owner for each data source, and the warehouse table it will read from.');
  prepare.push('A short list of numbers that must match exactly at cutover, and who signs them off.');
  if (has('rls')) prepare.push('Test accounts for each security role, so access can be checked before launch.');
  if (has('embedded')) prepare.push('The host app team, since embedding changes code on their side too.');

  const headline = {
    Small: 'A focused move you can plan around one pilot',
    Medium: 'A phased migration, starting with a pilot',
    Large: 'A program: prepare the data, pilot, then move by team',
  }[size];

  return {
    source: src,
    input: i,
    points,
    size,
    sizeLevel: { Small: 1, Medium: 2, Large: 3 }[size],
    headline,
    risks,
    phases,
    prepare,
    rebuildCount: risks.filter((r) => r.level === 'Rebuild').length,
  };
}

export function searchFromInputs(raw, source) {
  const i = sanitize(raw, source);
  const params = new URLSearchParams();
  for (const key of Object.keys(LIMITS)) params.set(key, String(i[key]));
  params.set('warehouse', i.warehouse);
  params.set('usageKnown', i.usageKnown);
  if (i.features.length) params.set('features', i.features.join(','));
  return `?${params.toString()}`;
}

export function inputsFromSearch(search, source) {
  const params = new URLSearchParams(search || '');
  const raw = { ...DEFAULTS, features: [] };
  for (const key of Object.keys(LIMITS)) {
    const v = params.get(key);
    if (v !== null && v.trim() !== '' && Number.isFinite(Number(v))) raw[key] = Number(v);
  }
  if (params.get('warehouse')) raw.warehouse = params.get('warehouse');
  if (params.get('usageKnown')) raw.usageKnown = params.get('usageKnown');
  if (params.get('features')) raw.features = params.get('features').split(',');
  return sanitize(raw, source);
}

export function hasResultParams(search) {
  return new URLSearchParams(search || '').has('reports');
}
